import React, { useCallback, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { Icons, Stack } from '@curves/ui-base';
import { PageLayout } from '@Local/Components/Layout';
import { Overview } from '@curves/ui-shared';
import useOverview from '@Util/hook/useOverview';
import useOptions from '@Util/hook/useOptions';
import useHasMenuPermission from '@Util/hook/useHasMenuPermission';
import { FRONT_KEY } from '@Constants/frontKey';
import useProductApi from '@Apis/useProductApi';
import { PRODUCT_ADD_ROUTER } from '@Router/routerPath';
import ProductListSearchBar from '@Local/Product/List/ProductListSearchBar';
import productTableColumns from '@Local/Product/List/productTableColumns';

const DEFAULT_SEARCH_PARAMS = {
    searchText: '',
    isEnabled: '0',
    categoryID: '0',
    pageIndex: 1,
    pageSize: 20,
};

/**
 * 商品總覽頁 `/site/product/overview`
 */
const ProductOverviewPage = () => {
    const { getProductOptionsApi, postProductOverviewApi } = useProductApi();
    // 取得商品選項（列表頁分類含「全部」）
    const getOptionsApi = useCallback(() => getProductOptionsApi({ skipAll: false }), [getProductOptionsApi]);
    // 商品選項（分類樹、上下架選項）
    const { options } = useOptions({ getOptionsApi, isFetchOnMount: true });

    // 搜尋列欄位（非受控，按搜尋時讀值）
    const searchTextRef = useRef(null);
    const isEnabledRef = useRef(null);
    const categoryRef = useRef(null);

    const { overviewData, searchParams, getOverviewData, updateOverviewPage } = useOverview({
        getOverviewApi: postProductOverviewApi,
        defaultSearchParams: DEFAULT_SEARCH_PARAMS,
        isFetchOnMount: true,
        enableQueryString: true,
    });

    // 組出搜尋條件，送出搜尋時回到第一頁
    const handleSearchSubmit = useCallback(() => {
        return {
            ...searchParams,
            searchText: searchTextRef.current?.getResult?.() ?? '',
            isEnabled: isEnabledRef.current?.getResult?.() ?? '',
            categoryID: categoryRef.current?.getResult?.() ?? '',
            pageIndex: 1,
        };
    }, [searchParams]);

    // 上下架後依目前條件重新查詢列表
    const refreshOverview = useCallback(() => getOverviewData(searchParams), [getOverviewData, searchParams]);

    const contextValue = {
        overviewData,
        searchParams,
        getOverviewData,
        updateOverviewPage,
    };

    // 是否有新增／編輯權限
    const canEdit = useHasMenuPermission(FRONT_KEY.PRODUCT_EDIT);

    const pageActions = canEdit ? [{ label: '新增商品', variant: 'contained', startIcon: <Icons.Plus />, as: NavLink, to: PRODUCT_ADD_ROUTER }] : [];

    return (
        <PageLayout>
            <PageLayout.Header title="商品總覽" actions={pageActions} />

            <Overview contextValue={contextValue} idKey="productID">
                <Overview.Search className="p-3 mb-3" getSearchParamsOnSubmit={handleSearchSubmit}>
                    <ProductListSearchBar
                        searchTextRef={searchTextRef}
                        isEnabledRef={isEnabledRef}
                        categoryRef={categoryRef}
                        searchParams={searchParams}
                        options={options}
                    />
                </Overview.Search>

                <Stack direction="row" justifyContent="end" alignItems="center" className="mb-2">
                    <Overview.TotalCount className="mb-0" showCurrentCount />
                </Stack>

                <Overview.Table
                    className="mb-3"
                    headerRow={productTableColumns}
                    cellRenderContext={{ refreshOverview, canEdit }}
                />
                <Overview.Pagination />
            </Overview>
        </PageLayout>
    );
};

export default ProductOverviewPage;
